import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Grid,
  TextField,
  Button,
  IconButton,
  Skeleton,
  Chip,
  FormControlLabel,
  Checkbox,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  ListItemText,
} from "@mui/material";
import { useIntl } from "react-intl";

import AddIcon from "@mui/icons-material/Add";
import DeleteForeverOutlinedIcon from "@mui/icons-material/DeleteForeverOutlined";

import TitledCard from "../TitledCard";
import useEditedStyles from "../../../../hooks/useEditedStyles";
import { uuidv4 } from "../../../../utils/utils";
import { Partition } from "./types";
import { hexInputCheck } from "../utils";
import { useSmartalarm } from "./SmartalarmTemplate";

const hideFollowerPartitions = true;

interface PartitionListProps {
  value: Partition[] | null;
  saved: Partition[] | null;
  onChange: (value: Partition[]) => void;
  disabled?: boolean;
}

const PartitionList = ({ value, saved, onChange, disabled }: PartitionListProps) => {
  const [indexes, setIndexes] = useState<number[]>([]);

  const { formatMessage } = useIntl();

  const onPartitionChange = useCallback(
    (index: number, v: Partition) => {
      if (!value) {
        return;
      }
      value[index] = v;
      onChange(value);
    },
    [value, onChange]
  );

  const addNewPartition = useCallback(() => {
    if (!value) {
      return;
    }
    value.push({ uuid: uuidv4(), name: "", code: "", home: false });
    onChange(value);
    setIndexes(value.map((v, i) => i));
  }, [value, onChange]);

  const removePartition = useCallback(
    (index) => {
      if (!value) {
        return;
      }
      value[index].deleted = true;
      onChange(value);
    },
    [value, onChange]
  );

  const restorePartition = useCallback(
    (index) => {
      if (!value) {
        return;
      }
      delete value[index].deleted;
      onChange(value);
    },
    [value, onChange]
  );

  useEffect(() => {
    if (!value) {
      return;
    }
    setIndexes(value.map((_, i) => i));
  }, [value]);

  // const overview = useMemo(() => {
  //   if (!value || indexes.length === 0) {
  //     return null;
  //   }
  //   return (
  //     <Grid container spacing={0.5} sx={{ padding: 1 }}>
  //       {indexes?.length === value.length &&
  //         value.map((partition: CodeNameValue) => (
  //           <Grid item key={`partition-${partition.uuid}`}>
  //             <HTChip
  //               label={[partition.deleted && `${formatMessage({ defaultMessage: "deleted" }).toUpperCase()} |`, partition.name].filter((p) => !!p).join(" ")}
  //               // code={indexToCode(index)}
  //               code={partition.code}
  //             />
  //           </Grid>
  //         ))}
  //     </Grid>
  //   );
  // }, [indexes, value, formatMessage]);

  const editView = useMemo(() => {
    if (!value) {
      return null;
    }
    return (
      <Grid container spacing={1} style={{ padding: 8 }}>
        {indexes?.length === value.length &&
          indexes.map((index: number) => (
            <Grid item xs={12} key={`partition-${value[index].uuid}`}>
              <PartitionListItem
                value={value[index]}
                saved={saved?.[index] || null}
                onChange={(value: Partition) => onPartitionChange(index, value)}
                onDelete={() => removePartition(index)}
                onRestore={() => restorePartition(index)}
                disabled={disabled}
              />
            </Grid>
          ))}
        <Grid item>
          <Button startIcon={<AddIcon />} onClick={() => addNewPartition()} disabled={disabled}>
            {formatMessage({ defaultMessage: "New partition" })}
          </Button>
        </Grid>
      </Grid>
    );
  }, [indexes, value, saved, disabled, formatMessage, onPartitionChange, addNewPartition, removePartition, restorePartition]);

  if (!value) {
    return (
      <TitledCard title={formatMessage({ defaultMessage: "Partitions" })}>
        <Grid container spacing={1} sx={{ padding: 1 }}>
          <Grid item>
            <Skeleton variant="rectangular" height={32} width={110} sx={{ borderRadius: 16 }} />
          </Grid>
          <Grid item>
            <Skeleton variant="rectangular" height={32} width={110} sx={{ borderRadius: 16 }} />
          </Grid>
        </Grid>
      </TitledCard>
    );
  }

  return (
    <TitledCard
      title={formatMessage({ defaultMessage: "Partitions" })}
      // modes={[
      //   { icon: <RemoveRedEyeOutlinedIcon fontSize="small" />, content: overview },
      //   {
      //     icon: disabled ? (
      //       <Badge
      //         badgeContent={<RemoveRedEyeOutlinedIcon fontSize="inherit" />}
      //         anchorOrigin={{
      //           vertical: "bottom",
      //           horizontal: "right",
      //         }}
      //       >
      //         <EditOutlinedIcon fontSize="small" />
      //       </Badge>
      //     ) : (
      //       <EditOutlinedIcon fontSize="small" />
      //     ),
      //     content: editView,
      //   },
      // ]}
    >
      {editView}
    </TitledCard>
  );
};

export const indexToCode = (index: number) => {
  return (index + 1).toString(16).toUpperCase().padStart(2, "0");
};

const PartitionListItem = ({
  value,
  saved,
  onChange,
  onDelete,
  onRestore,
  disabled,
}: {
  value: Partition;
  saved: Partition | null;
  onChange: (value: Partition) => void;
  onDelete: () => void;
  onRestore: () => void;
  disabled?: boolean;
}) => {
  const [name, setName] = useState(value?.name || "");
  const [code, setCode] = useState(value?.code || "");
  const [home, setHome] = useState(value?.home ?? false);
  // const [technical, setTechnical] = useState(value?.technical || false);
  const [followedPartitions, setFollowedPartitions] = useState(value?.followedPartitions ?? []);
  const [partitions, setPartitions] = useState<Partition[]>([]);

  const editedStyle = useEditedStyles();
  const { formatMessage } = useIntl();

  const {
    value: {
      plists: { partitions: allPartitions },
    },
  } = useSmartalarm();

  const handleHome = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!value) {
        return;
      }
      setHome(event.target.checked);
      value.home = event.target.checked;
      onChange(value);
    },
    [value, onChange]
  );

  // const handleTechnical = useCallback(
  //   (event: React.ChangeEvent<HTMLInputElement>) => {
  //     if (!value) {
  //       return;
  //     }
  //     setTechnical(event.target.checked);
  //     value.technical = event.target.checked;
  //     onChange(value);
  //   },
  //   [value, onChange]
  // );

  const handleFollowedPartitionsChange = useCallback(
    (event: SelectChangeEvent<string[]>) => {
      if (!value) {
        return;
      }
      setFollowedPartitions(event.target.value as string[]);
      value.followedPartitions = event.target.value as string[];
      onChange(value);
    },
    [value, onChange]
  );

  useEffect(() => {
    if (!allPartitions || !value) {
      return;
    }
    setPartitions(allPartitions.filter(({ uuid, code }) => uuid !== value.uuid && code));
  }, [allPartitions, value]);

  return (
    <Grid container spacing={1} alignItems="center">
      <Grid item>
        <TextField
          label={formatMessage({ defaultMessage: "Partition" })}
          variant="filled"
          value={code}
          className={saved?.code !== code ? editedStyle.input : ""}
          inputProps={{ maxLength: 2, style: { textTransform: "uppercase" } }}
          onChange={(e) => hexInputCheck(e.target.value, setCode)}
          onBlur={() => {
            const v = code ? code.padStart(2, "0") : code;
            value.code = v;
            setCode(v);
            onChange(value);
          }}
          sx={{ minWidth: 80, width: 80, margin: 0 }}
          size="small"
          autoComplete="off"
          disabled={disabled || value.deleted}
        />
      </Grid>
      <Grid item>
        <TextField
          label={formatMessage({ defaultMessage: "Name" })}
          variant="filled"
          value={name}
          className={saved?.name !== name ? editedStyle.input : ""}
          onChange={(e) => setName(e.target.value)}
          onBlur={() => {
            value.name = name;
            onChange(value);
          }}
          sx={{ margin: 0 }}
          size="small"
          autoComplete="off"
          disabled={disabled || value?.deleted}
        />
      </Grid>
      <Grid item>
        <FormControlLabel
          control={<Checkbox checked={home} color="secondary" onChange={handleHome} style={{ padding: 1, margin: 1 }} />}
          label={
            <Typography variant="caption" sx={{ paddingRight: (theme) => theme.spacing(0.5) }}>
              {formatMessage({ defaultMessage: "Stay mode" })}
            </Typography>
          }
          labelPlacement="end"
          sx={{ m: 0 }}
          className={saved?.home !== home ? editedStyle.background : ""}
          disabled={disabled}
        />
      </Grid>
      {/* <Grid item>
        <FormControlLabel
          control={<Checkbox checked={technical} color="secondary" onChange={handleTechnical} style={{ padding: 1, margin: 1 }} />}
          label={<Typography variant="caption">{formatMessage({ defaultMessage: "Technical" })}</Typography>}
          labelPlacement="end"
          sx={{ m: 0 }}
          className={saved?.technical !== technical ? editedStyle.background : ""}
          disabled={disabled}
        />
      </Grid> */}
      {!hideFollowerPartitions && (
        <Grid item>
          <FormControl
            variant="filled"
            size="small"
            sx={{ margin: 0, width: 200 }}
            // className={saved?.followedPartitions !== followedPartitions || partitions.find((p) => p.uuid === followedPartitions)?.deleted ? editedStyle.input : ""}
            // disabled={disabled || users.filter((u) => !u.deleted).length === 0 || value?.deleted}
            disabled={disabled || partitions.length === 0}
            fullWidth
          >
            <InputLabel id="select-label">{formatMessage({ defaultMessage: "Followed partitions" })}</InputLabel>
            <Select
              labelId="select-label"
              value={followedPartitions}
              multiple
              onChange={handleFollowedPartitionsChange}
              renderValue={(selected) => {
                if (selected.length === 0) {
                  return "";
                }
                return partitions
                  .filter(({ uuid }) => selected.includes(uuid))
                  .map(({ code, name }) => [code, name].filter((i) => !!i).join(" "))
                  .join(", ");
              }}
            >
              {partitions.map((option) => (
                <MenuItem key={`option-${option.uuid}`} value={option.uuid} disabled={option.deleted}>
                  <Checkbox checked={followedPartitions.includes(option.uuid)} />
                  <ListItemText
                    primary={[option.deleted ? `${formatMessage({ defaultMessage: "deleted" }).toUpperCase()} |` : "", option.code, option.name]
                      .filter((i) => !!i)
                      .join(" ")}
                  />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      )}
      {!value?.deleted && !disabled && (
        <Grid item>
          <IconButton onClick={() => onDelete()}>
            <DeleteForeverOutlinedIcon />
          </IconButton>
        </Grid>
      )}
      {value?.deleted && !disabled && (
        <>
          <Grid item>
            <Chip label={formatMessage({ defaultMessage: "deleted" })} size="small" className={editedStyle.chip} />
          </Grid>
          <Grid item>
            <Button size="small" onClick={onRestore} color="inherit">
              {formatMessage({ defaultMessage: "restore" })}
            </Button>
          </Grid>
        </>
      )}
    </Grid>
  );
};

export default PartitionList;
